/*
  Component: Sticky header — scroll state
  Responsibility: decide whether the header is pinned, and in "scroll up" mode whether it
  is currently out of the way. Nothing else: the pinning itself, the slide and the shadow
  are all assets/kl-sticky-header.css, which reads the two classes written here.

  No layout value is ever written. The header's resting offset is read, not set, and it is
  re-read whenever the header is seen below the top of the viewport, so an announcement bar
  that changes height — or is dismissed — cannot leave a stale threshold behind.
*/

const KL_STICKY_HEADER_SELECTOR = '[data-kl-sticky-header]';
const KL_STICKY_CLASS = 'is-sticky';
const KL_STICKY_HIDDEN_CLASS = 'is-sticky-hidden';

// Enough to ignore the jitter of a trackpad or a rubber-banding phone, small enough that a
// deliberate flick still registers on the first frame.
const KL_STICKY_SCROLL_TOLERANCE = 6;

function getScrollTop() {
  return window.scrollY || document.documentElement.scrollTop || 0;
}

function initStickyHeader(headerElement) {
  if (headerElement.dataset.klStickyHeaderReady === 'true') return;

  const stickyMode = headerElement.dataset.klStickyHeader;

  if (stickyMode !== 'scroll-up' && stickyMode !== 'always') return;

  headerElement.dataset.klStickyHeaderReady = 'true';

  const canHide = stickyMode === 'scroll-up';

  let restingOffset = 0;
  let lastScrollTop = getScrollTop();
  let pendingFrame = null;

  /*
    Only meaningful while the header still sits below the top of the viewport — once it is
    pinned its box is at 0 and says nothing about where it came from. Called on every frame
    the header is loose, so the threshold tracks whatever sits above it.
  */
  function refreshRestingOffset() {
    const distanceFromTop = headerElement.getBoundingClientRect().top;

    if (distanceFromTop > 0) restingOffset = distanceFromTop + getScrollTop();
  }

  function teardown() {
    window.removeEventListener('scroll', requestState);
    window.removeEventListener('resize', requestState);
    window.removeEventListener('hashchange', requestState);
  }

  function applyState() {
    pendingFrame = null;

    // The theme editor swaps the whole section out; the replacement runs its own init, so
    // this copy has nothing left to do but let go of the window.
    if (!headerElement.isConnected) {
      teardown();
      return;
    }

    const scrollTop = getScrollTop();

    refreshRestingOffset();

    const isPinned = scrollTop > restingOffset;

    headerElement.classList.toggle(KL_STICKY_CLASS, isPinned);

    if (!canHide || !isPinned) {
      headerElement.classList.remove(KL_STICKY_HIDDEN_CLASS);
      lastScrollTop = scrollTop;
      return;
    }

    const scrollDelta = scrollTop - lastScrollTop;

    if (Math.abs(scrollDelta) < KL_STICKY_SCROLL_TOLERANCE) return;

    lastScrollTop = scrollTop;

    // Tabbing through the header must not slide it away under the visitor's cursor: some
    // browsers scroll the page to bring a focused control into view.
    if (scrollDelta > 0 && headerElement.contains(document.activeElement)) return;

    headerElement.classList.toggle(KL_STICKY_HIDDEN_CLASS, scrollDelta > 0);
  }

  function requestState() {
    if (pendingFrame !== null) return;

    pendingFrame = requestAnimationFrame(applyState);
  }

  window.addEventListener('scroll', requestState, { passive: true });
  window.addEventListener('resize', requestState, { passive: true });

  // An in-page jump lands the visitor mid-document with no scroll event of its own; without
  // this the header would stay hidden from whatever the previous scroll direction was.
  window.addEventListener('hashchange', requestState);

  applyState();
}

function initStickyHeaders(rootElement) {
  rootElement.querySelectorAll(KL_STICKY_HEADER_SELECTOR).forEach(initStickyHeader);
}

initStickyHeaders(document);

// Theme editor: a re-rendered section arrives with fresh, uninitialised markup.
document.addEventListener('shopify:section:load', (event) => {
  initStickyHeaders(event.target);
});
